import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { MediaService } from '../../services/mediaService';
import { WatchlistService } from '../../services/watchlistService';
import { SocialService } from '../../services/socialService';
import { useAuth } from '../../context/AuthContext';
import { useAuthGate } from '../../hooks/useAuthGate';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import AuthModal from '../../components/AuthModal/AuthModal';
import CuratedListRail from '../../components/CuratedLists/CuratedListRail';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { getMediaProgressionState, TIER_COLORS } from '../../utils/mediaProgression';
import styles from './Discover.module.css';
import { FoundingCollectorIcon } from '../../components/FoundingCollectorBadge/FoundingCollectorBadge';
import SaveToListModal from '../../components/MyLists/SaveToListModal';
import TopMoviesDiscoverCard from '../../components/TopMovies/TopMoviesDiscoverCard';

// ─── Constants ─────────────────────────────────────────────────────────────────

const SESSION_FEED_TAB_KEY = 'discover_feed_tab';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSourceSection({ feedTab, searchQuery, isCategoryView }) {
    if (searchQuery?.trim()) return 'search';
    if (isCategoryView) return 'category';
    if (feedTab === 'aluvo-picks') return 'aluvo_picks';
    if (feedTab === 'trending-to-verify') return 'trending_to_verify';
    if (feedTab === 'trending-now') return 'trending_now';
    return 'other';
}

// ─── Trending-to-Verify card ──────────────────────────────────────────────────

function handlePosterKeyDown(event, item, onOpenDetails) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpenDetails(item);
    }
}

function VerifyCard({ item, onVerify, onOpenDetails, isGuest, sourceSection }) {
    const posterUrl = item.posterUrl ?? null;
    const title     = item.title;
    const progression = getMediaProgressionState({
        progress: item.userProgress ?? {
            tier: item.userTier,
            canUpgrade: item.canUpgrade,
        },
        isGuest,
    });

    let verifyLabel = null;
    if (item.verificationCount != null && item.verificationCount > 0) {
        const window = item.verificationWindow === 'month' ? 'this month' : 'this week';
        verifyLabel = `${item.verificationCount.toLocaleString()} verified ${window}`;
    } else if (item.displayLabel) {
        verifyLabel = item.displayLabel;
    }

    const hasRealCount = item.verificationCount != null && item.verificationCount > 0;

    return (
        <div className={styles.verifyCard}>
            <div
                className={styles.verifyPosterWrapper}
                role="button"
                tabIndex={0}
                aria-label={`View details for ${title}`}
                onClick={() => onOpenDetails(item)}
                onKeyDown={(event) => handlePosterKeyDown(event, item, onOpenDetails)}
            >
                {posterUrl ? (
                    <img src={posterUrl} alt={title} className={styles.verifyPoster} loading="lazy" />
                ) : (
                    <div className={styles.posterFallback}>🎬</div>
                )}
            </div>

            <div className={styles.verifyCardInfo}>
                <h3 className={styles.verifyCardTitle} title={title}>{title}</h3>

                {verifyLabel && (
                    <p className={styles.verifyCount}>
                        <span className={styles.verifyCountIcon}>{hasRealCount ? '🔥' : '✨'}</span>
                        {verifyLabel}
                    </p>
                )}

                <div className={styles.verifyActions}>
                    {progression.badgeLabel && (
                            <span
                                className={styles.tierBadgeInline}
                                style={{
                                    color: TIER_COLORS[progression.currentTier],
                                    borderColor: TIER_COLORS[progression.currentTier],
                                }}
                            >
                                {progression.badgeLabel}
                            </span>
                    )}
                    <button
                        className={progression.isComplete ? styles.completeBtn : progression.currentTier ? styles.upgradeBtn : styles.verifyBtn}
                        onClick={() => onVerify(item, progression, sourceSection)}
                    >
                        {progression.ctaLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Content card (Trending Now) ──────────────────────────────────────────────

function ContentCard({ item, isInWatchlist, onWatchlist, onAddToList, onQuiz, onOpenDetails, isLastCard, cardRef, isGuest, sourceSection }) {
    const posterUrl   = item.posterUrl ?? null;
    const title       = item.title;
    const year        = item.year;
    const rating      = item.voteAverage?.toFixed(1) ?? null;
    const hasLegendary  = item.hasLegendary ?? false;
    const verifiedCount = item.verifiedCount ?? null;
    const lovedPct      = item.lovedPct ?? null;
    const collections   = item.collections ?? [];
    const progression = getMediaProgressionState({ progress: item.userProgress, isGuest });

    return (
        <div
            className={`${styles.card} ${hasLegendary ? styles.cardLegendary : ''}`}
            ref={isLastCard ? cardRef : null}
        >
            <div
                className={styles.posterWrapper}
                role="button"
                tabIndex={0}
                aria-label={`View details for ${title}`}
                onClick={() => onOpenDetails(item)}
                onKeyDown={(event) => handlePosterKeyDown(event, item, onOpenDetails)}
            >
                {posterUrl ? (
                    <img src={posterUrl} alt={title} className={styles.poster} loading="lazy" />
                ) : (
                    <div className={styles.posterFallback}>🎬</div>
                )}
                {hasLegendary && (
                    <div className={styles.legendaryBadge}>✦ Legendary</div>
                )}
                {progression.badgeLabel && (
                    <div
                        className={`${styles.progressionBadge} ${progression.currentTier === 'legendary' ? styles.progressionBadgeLegendary : ''}`}
                        style={{
                            color: TIER_COLORS[progression.currentTier],
                            borderColor: TIER_COLORS[progression.currentTier],
                            top: hasLegendary ? 38 : 10,
                        }}
                    >
                        {progression.badgeLabel}
                    </div>
                )}
                <div className={styles.overlay} aria-hidden="true">
                    <span className={styles.detailsBtn}>View Details</span>
                </div>
            </div>

            <div className={styles.cardInfo}>
                <h3 className={styles.cardTitle} title={title}>{title}</h3>

                <div className={styles.cardMeta}>
                    {rating && <span className={styles.rating}>⭐ {rating}</span>}
                    {year    && <span className={styles.year}>{year}</span>}
                    <span className={styles.kindPill}>{item.kind === 'tv' ? 'TV' : 'Film'}</span>
                </div>

                <div className={styles.aluvoStats}>
                    {verifiedCount != null && (
                        <span className={styles.statChip}>👁 {verifiedCount.toLocaleString()} verified</span>
                    )}
                    {lovedPct != null && (
                        <span className={styles.statChip}>🔥 {lovedPct}% Loved It</span>
                    )}
                    {collections.length > 0 && (
                        <span className={styles.statChip} title={collections.join(', ')}>
                            🏛️ {collections.length > 1 ? `${collections.length} collections` : collections[0]}
                        </span>
                    )}
                </div>

                <div className={styles.cardActions}>
                    <button
                        className={`${styles.quizBtn} ${progression.isComplete ? styles.quizBtnComplete : ''}`}
                        onClick={() => onQuiz(item, progression, sourceSection)}
                        title={progression.ctaLabel}
                    >
                        {progression.ctaLabel}
                    </button>
                    <button
                        className={`${styles.watchlistBtn} ${isInWatchlist ? styles.inWatchlist : ''}`}
                        onClick={() => onWatchlist(item)}
                        title={isInWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                    >
                        {isInWatchlist ? '✓' : '+'}
                    </button>
                    {onAddToList && (
                        <button
                            className={styles.listBtn}
                            onClick={() => onAddToList(item)}
                            title="Add to List"
                            aria-label={`Add ${title} to a list`}
                        >
                            ☰
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── User card ────────────────────────────────────────────────────────────────

function UserCard({ userItem, isFollowing, isCurrentUser, onFollow }) {
    const initials = (userItem.display_name || userItem.username).charAt(0).toUpperCase();

    const coinCount        = userItem.coin_count       ?? 0;
    const legendaryCount   = userItem.legendary_count  ?? 0;
    const completedCount   = userItem.completed_collections ?? 0;
    const recentAchievement = userItem.recent_achievement ?? null;
    const statusLabel      = userItem.status_label     ?? null;

    return (
        <div className={styles.userCard}>
            <Link to={`/user/${userItem.username}`} className={styles.userCardLink}>
                <div className={styles.userAvatar}>
                    {userItem.avatar_url ? (
                        <img src={userItem.avatar_url} alt={userItem.display_name || userItem.username} />
                    ) : (
                        <div className={styles.avatarFallback}>{initials}</div>
                    )}
                    {legendaryCount > 0 && <div className={styles.legendaryRing} />}
                </div>
                <div className={styles.userInfo}>
                    <h3 className={styles.userName}>
                        {userItem.display_name || userItem.username}
                        <FoundingCollectorIcon isFoundingCollector={userItem.isFoundingCollector ?? userItem.is_founding_collector} />
                    </h3>
                    <p className={styles.userUsername}>@{userItem.username}</p>
                    {statusLabel && <span className={styles.statusLabel}>{statusLabel}</span>}
                    <div className={styles.userStats}>
                        <span className={styles.coinCount}>🪙 {coinCount}</span>
                        {legendaryCount > 0 && <span className={styles.legendaryCount}>✦ {legendaryCount} Legendary</span>}
                        {completedCount > 0 && <span className={styles.collectionCount}>🏛️ {completedCount}</span>}
                    </div>
                    {recentAchievement && (
                        <p className={styles.recentAchievement}>
                            Recently earned: <strong>{recentAchievement.tier}</strong> — {recentAchievement.title}
                        </p>
                    )}
                </div>
            </Link>
            {!isCurrentUser && (
                <div className={styles.userActions}>
                    <Link to={`/user/${userItem.username}`} className={styles.viewProfileBtn}>
                        View Profile
                    </Link>
                    <button
                        className={`${styles.followBtn} ${isFollowing ? styles.followingBtn : ''}`}
                        onClick={onFollow}
                    >
                        {isFollowing ? '✓ Following' : '+ Follow'}
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── Logged-out hero banner ───────────────────────────────────────────────────

function GuestBanner() {
    const navigate = useNavigate();
    return (
        <div className={styles.guestBanner}>
            <div className={styles.guestBannerInner}>
                <span className={styles.guestBannerIcon}>🪙</span>
                <div className={styles.guestBannerText}>
                    <strong>Aluvo is live.</strong> Verified viewers are earning coins right now.
                </div>
                <button className={styles.guestBannerCta} onClick={() => navigate('/register')}>
                    Join Free →
                </button>
            </div>
        </div>
    );
}

export function BrowseCategories({
    categories,
    activeCategoryId,
    activeCategory,
    isCategoryView,
    isPickerOpen,
    onTogglePicker,
    onClear,
    onSelect,
}) {
    if (!categories.length) return null;

    if (isCategoryView && activeCategory) {
        return (
            <section className={styles.categorySection} aria-label="Selected category">
                <div className={styles.selectedCategoryBar}>
                    <div className={styles.selectedCategoryLabel} aria-live="polite">
                        <span className={styles.categoryIcon} aria-hidden="true">{activeCategory.icon}</span>
                        <span>{activeCategory.label}</span>
                    </div>
                    <div className={styles.selectedCategoryActions}>
                        <button
                            type="button"
                            className={styles.changeCategoryBtn}
                            onClick={onTogglePicker}
                            aria-expanded={isPickerOpen}
                            aria-controls="discover-category-picker"
                        >
                            Change ▾
                        </button>
                        <button type="button" className={styles.clearCategoryBtn} onClick={onClear}>
                            Clear
                        </button>
                    </div>
                </div>
                {isPickerOpen && (
                    <div id="discover-category-picker" className={styles.categoryMenu} aria-label="Choose a category">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                type="button"
                                className={`${styles.categoryChip} ${activeCategoryId === category.id ? styles.categoryChipActive : ''}`}
                                onClick={() => onSelect(category)}
                                aria-pressed={activeCategoryId === category.id}
                            >
                                <span className={styles.categoryIcon} aria-hidden="true">{category.icon}</span>
                                <span>{category.label}</span>
                            </button>
                        ))}
                    </div>
                )}
            </section>
        );
    }

    return (
        <section className={styles.categorySection} aria-label="Browse categories">
            <div className={styles.categoryScroller}>
                {categories.map((category) => (
                    <button
                        key={category.id}
                        type="button"
                        className={`${styles.categoryChip} ${activeCategoryId === category.id ? styles.categoryChipActive : ''}`}
                        onClick={() => onSelect(category)}
                        aria-pressed={activeCategoryId === category.id}
                    >
                        <span className={styles.categoryIcon} aria-hidden="true">{category.icon}</span>
                        <span>{category.label}</span>
                    </button>
                ))}
            </div>
        </section>
    );
}

// ─── Main Discover page ───────────────────────────────────────────────────────

function Discover() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { categoryId } = useParams();
    const { gateAction, authModalProps } = useAuthGate(user);

    // ── Top-level tab: Movies&TV vs Users ─────────────────────────────────────
    // `/discover?tab=people` opens Users directly (Home's Following tab links here).
    const [activeTab, setActiveTab]       = useState(() => {
        try {
            return new URLSearchParams(window.location.search).get('tab') === 'people' ? 'users' : 'content';
        } catch {
            return 'content';
        }
    }); // 'content' | 'users'
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchQuery, setSearchQuery]   = useState('');

    // ── Feed sub-tab: session-backed, defaults to Aluvo Picks on fresh launch ──
    const [feedTab, setFeedTab] = useState(() => {
        try {
            return sessionStorage.getItem(SESSION_FEED_TAB_KEY) || 'aluvo-picks';
        } catch {
            return 'aluvo-picks';
        }
    });

    const handleFeedTabChange = (tab) => {
        setFeedTab(tab);
        try { sessionStorage.setItem(SESSION_FEED_TAB_KEY, tab); } catch {}
    };

    // ── Data state ────────────────────────────────────────────────────────────
    const [verifyFeed, setVerifyFeed]       = useState([]);
    const [verifyLoading, setVerifyLoading] = useState(true);
    const [verifyError, setVerifyError]     = useState(null);

    const [content, setContent]         = useState([]);
    const [categories, setCategories]   = useState([]);
    const [categoryMeta, setCategoryMeta] = useState(null);
    const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
    const [users, setUsers]             = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [watchlistIds, setWatchlistIds] = useState(new Set());
    const [saveToListTarget, setSaveToListTarget] = useState(null);
    const [followingIds, setFollowingIds] = useState(new Set());
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore]         = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const observerRef = useRef(null);

    const isCategoryView = activeTab === 'content' && !!categoryId && !searchQuery.trim();

    // ── Load Trending-to-Verify once on mount (and whenever content tab re-activates) ──
    useEffect(() => {
        if (activeTab === 'content') loadTrendingToVerify();
    }, [activeTab]);

    // ── Load Trending Now / Users on filter or tab change ────────────────────
    useEffect(() => {
        if (isCategoryView) return;
        setCurrentPage(1);
        setHasMore(true);
        if (activeTab === 'content' && feedTab !== 'aluvo-picks') {
            loadTrending(1, true);
        } else if (activeTab === 'users') {
            loadUsers();
        } else {
            setLoading(false);
        }
    }, [activeFilter, activeTab, isCategoryView, feedTab]);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        if (!categoryId) {
            setCategoryMeta(null);
            return;
        }
        setActiveTab('content');
        setCurrentPage(1);
        setHasMore(true);
        loadCategory(categoryId, 1, true);
    }, [categoryId, activeFilter]);

    useEffect(() => {
        if (user) loadWatchlist();
    }, [user]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            if (isCategoryView) return;
            setCurrentPage(1);
            setHasMore(true);
            if (activeTab === 'content' && feedTab !== 'aluvo-picks') {
                loadTrending(1, true);
            } else if (activeTab === 'users') {
                loadUsers();
            } else {
                setLoading(false);
            }
            return;
        }
        const timer = setTimeout(() => {
            setCurrentPage(1);
            setHasMore(true);
            performSearch(1, true);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, activeTab, isCategoryView, feedTab]);

    // ── Data fetchers ─────────────────────────────────────────────────────────

    const loadTrendingToVerify = async () => {
        try {
            setVerifyLoading(true);
            setVerifyError(null);
            const data = await MediaService.getTrendingToVerify();
            setVerifyFeed(data.results || []);
        } catch (err) {
            setVerifyError(err.message);
        } finally {
            setVerifyLoading(false);
        }
    };

    const loadTrending = async (page = 1, reset = false) => {
        try {
            reset ? setLoading(true) : setLoadingMore(true);
            setError(null);
            const data = await MediaService.getTrending(activeFilter, page);
            if (reset) {
                setContent(data.results || []);
            } else {
                setContent(prev => [...prev, ...(data.results || [])]);
            }
            setHasMore(data.results?.length > 0 && data.page < data.totalPages);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadCategories = async () => {
        try {
            const data = await MediaService.getCategories();
            setCategories(data.categories || []);
        } catch {
            setCategories([]);
        }
    };

    const loadCategory = async (id, page = 1, reset = false) => {
        try {
            reset ? setLoading(true) : setLoadingMore(true);
            setError(null);
            const data = await MediaService.getCategory(id, { kind: activeFilter, page });
            setCategoryMeta(data.category || null);
            if (reset) {
                setContent(data.results || []);
            } else {
                setContent(prev => [...prev, ...(data.results || [])]);
            }
            setHasMore(data.results?.length > 0 && data.page < data.totalPages);
        } catch (err) {
            setError(err.message);
            if (reset) {
                setContent([]);
                setCategoryMeta(null);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await SocialService.getUsers();
            setUsers(data || []);
            if (user && data?.length > 0) {
                const followingSet = new Set();
                await Promise.all(
                    data.map(async (u) => {
                        try {
                            const status = await SocialService.getFollowStatus(u.id);
                            if (status?.following) followingSet.add(u.id);
                        } catch {}
                    })
                );
                setFollowingIds(followingSet);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const performSearch = async (page = 1, reset = false) => {
        try {
            reset ? setLoading(true) : setLoadingMore(true);
            setError(null);
            if (activeTab === 'content') {
                const data = await MediaService.search(searchQuery, page);
                if (reset) {
                    setContent(data.results || []);
                } else {
                    setContent(prev => [...prev, ...(data.results || [])]);
                }
                setHasMore(data.results?.length > 0 && data.page < data.totalPages);
            } else {
                const data = await SocialService.searchUsers(searchQuery);
                setUsers(data || []);
                if (user && data?.length > 0) {
                    const followingSet = new Set();
                    await Promise.all(
                        data.map(async (u) => {
                            try {
                                const status = await SocialService.getFollowStatus(u.id);
                                if (status?.following) followingSet.add(u.id);
                            } catch {}
                        })
                    );
                    setFollowingIds(followingSet);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadWatchlist = async () => {
        try {
            const watchlist = await WatchlistService.getWatchlist();
            const ids = new Set(watchlist.items?.map(item => item.media?.tmdb_id) || []);
            setWatchlistIds(ids);
        } catch {
            // non-critical
        }
    };

    // ── Gated actions ──────────────────────────────────────────────────────────

    const _doToggleWatchlist = async (item) => {
        const isInWatchlist = watchlistIds.has(item.tmdbId);
        try {
            if (isInWatchlist) {
                const watchlist = await WatchlistService.getWatchlist();
                const watchlistItem = watchlist.items?.find(w => w.media?.tmdb_id === item.tmdbId);
                if (watchlistItem) {
                    await WatchlistService.removeFromWatchlist(watchlistItem.id);
                    setWatchlistIds(prev => { const next = new Set(prev); next.delete(item.tmdbId); return next; });
                }
            } else {
                await WatchlistService.addToWatchlist(item.tmdbId, item.kind);
                setWatchlistIds(prev => new Set([...prev, item.tmdbId]));
            }
        } catch {
            alert('Failed to update watchlist. Please try again.');
        }
    };

    const handleToggleWatchlist = (item) =>
        gateAction(() => _doToggleWatchlist(item), AUTH_MESSAGES.WATCHLIST)();

    // Optional secondary action on Discover cards — opens the "Save to a list" dialog.
    const handleAddToList = (item) =>
        gateAction(() => setSaveToListTarget({ mediaId: item.tmdbId, mediaType: item.kind, seasonNumber: null, title: item.title }), AUTH_MESSAGES.MY_LISTS)();

    const _doToggleFollow = async (userId) => {
        const isFollowing = followingIds.has(userId);
        try {
            if (isFollowing) {
                await SocialService.unfollow(userId);
                setFollowingIds(prev => { const next = new Set(prev); next.delete(userId); return next; });
            } else {
                await SocialService.follow(userId);
                setFollowingIds(prev => new Set([...prev, userId]));
            }
        } catch {
            alert('Failed to update follow status. Please try again.');
        }
    };

    const handleToggleFollow = (userId) =>
        gateAction(() => _doToggleFollow(userId), AUTH_MESSAGES.FOLLOW)();

    // Detail pages are public; verification and watchlist actions are gated on
    // the detail page itself.
    const trackDiscoverCardCta = (item, progression, sourceSection) => {
        trackEvent(ANALYTICS_EVENTS.DISCOVER_MEDIA_CARD_CTA_CLICKED, {
            media_id: item.tmdbId ?? item.mediaId,
            media_type: item.kind,
            title: item.title,
            current_coin_tier: progression?.currentTier ?? 'none',
            next_coin_tier: progression?.nextTier ?? 'bronze',
            action: progression?.action ?? 'verify',
            source_section: sourceSection || getSourceSection({ feedTab, searchQuery, isCategoryView }),
        });
    };

    const handleQuizOrDetail = (item, progression, sourceSection) => {
        trackDiscoverCardCta(item, progression, sourceSection);
        navigate(`/movie/${item.kind}/${item.tmdbId}`);
    };

    const handleOpenDetails = (item) => {
        navigate(`/movie/${item.kind}/${item.tmdbId}`);
    };

    const handleVerify = (item, progression, sourceSection) => {
        trackDiscoverCardCta(item, progression, sourceSection);
        navigate(`/movie/${item.kind}/${item.tmdbId}`);
    };

    // ── Infinite scroll (Trending Now only) ───────────────────────────────────

    const loadMoreContent = useCallback(() => {
        if (loadingMore || !hasMore || activeTab !== 'content') return;
        if (feedTab === 'aluvo-picks') return;
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        if (searchQuery.trim()) {
            performSearch(nextPage, false);
        } else if (categoryId) {
            loadCategory(categoryId, nextPage, false);
        } else {
            loadTrending(nextPage, false);
        }
    }, [loadingMore, hasMore, currentPage, searchQuery, activeTab, categoryId, activeFilter, feedTab]);

    const lastCardRef = useCallback((node) => {
        if (loading || loadingMore) return;
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) loadMoreContent();
        });
        if (node) observerRef.current.observe(node);
    }, [loading, loadingMore, hasMore, loadMoreContent]);

    // ── Derived ────────────────────────────────────────────────────────────────

    const filteredContent = content.filter(item => {
        if (activeFilter === 'all') return true;
        return item.kind === activeFilter;
    });
    const filteredVerifyFeed = verifyFeed.filter(item => {
        if (activeFilter === 'all') return true;
        return item.kind === activeFilter;
    });
    const sourceSection = getSourceSection({ feedTab, searchQuery, isCategoryView });
    const activeCategory = categoryMeta || categories.find(category => category.id === categoryId);

    const sectionHeading = searchQuery
        ? '🔍 Search Results'
        : isCategoryView
            ? `${categoryMeta?.icon || '🎬'} ${categoryMeta?.label || 'Category'}`
        : activeTab === 'users'
            ? '👥 Discover Collectors'
            : feedTab === 'aluvo-picks'
                ? 'Aluvo Picks'
            : feedTab === 'trending-to-verify'
                ? 'Trending on Aluvo'
            : activeFilter === 'movie' ? '🎬 Popular Movies'
                : activeFilter === 'tv' ? '📺 Popular TV Shows'
                        : '🌍 Trending Now';

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <main className={styles.page}>

            <AuthModal {...authModalProps} />
            <SaveToListModal
                isOpen={!!saveToListTarget}
                target={saveToListTarget}
                source="discover"
                onClose={() => setSaveToListTarget(null)}
                onSaved={(membership, { watchlistChanged, inWatchlist }) => {
                    if (!watchlistChanged || !membership?.target) return;
                    const id = membership.target.mediaId;
                    setWatchlistIds(prev => { const next = new Set(prev); if (inWatchlist) next.add(id); else next.delete(id); return next; });
                }}
            />

            {/*{!user && <GuestBanner />}*/}

            {/* Hero */}
            <section className={styles.hero} style={{ paddingTop: 'calc(var(--nav-height) + 40px)' }}>
                <div className={styles.container}>
                    <h1 className={styles.title}>
                        {activeTab === 'content'
                            ? <>Discover what <span className={styles.highlight}>verified viewers</span> are watching</>
                            : <>Meet the <span className={styles.highlight}>collectors</span></>
                        }
                    </h1>
                    <p className={styles.subtitle}>
                        {activeTab === 'content'
                            ? 'Browse trending titles. Watch. Verify. Earn collectible coins. Unlock Legendary.'
                            : 'Explore public profiles of verified collectors building their coin collections.'
                        }
                    </p>
                    {!user && (
                        <p className={styles.guestSubtext}>
                            Browsing as guest — <Link to="/register" className={styles.guestLink}>create a free account</Link> to earn coins.
                        </p>
                    )}
                </div>
            </section>

            {/* Sticky search / filter bar */}
            <section className={styles.searchSection} style={{ top: 'var(--nav-height)' }}>
                <div className={styles.container}>

                    {/* Top-level tabs: Movies & TV / Users */}
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'content' ? styles.activeTab : ''}`}
                            onClick={() => { setActiveTab('content'); setSearchQuery(''); }}
                        >
                            🎬 Movies & TV
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'users' ? styles.activeTab : ''}`}
                            onClick={() => { setActiveTab('users'); setSearchQuery(''); }}
                        >
                            👥 Users
                        </button>
                    </div>

                    {/* Feed sub-tabs — only shown in the content tab when not searching */}
                    {activeTab === 'content' && !searchQuery && (
                        <div className={styles.feedTabs}>
                            <button
                                className={`${styles.feedTab} ${feedTab === 'aluvo-picks' ? styles.feedTabActive : ''}`}
                                onClick={() => handleFeedTabChange('aluvo-picks')}
                            >
                                Aluvo Picks
                            </button>
                            <button
                                className={`${styles.feedTab} ${feedTab === 'trending-now' ? styles.feedTabActive : ''}`}
                                onClick={() => handleFeedTabChange('trending-now')}
                            >
                                🌍 Trending Now
                            </button>
                            <button
                                className={`${styles.feedTab} ${feedTab === 'trending-to-verify' ? styles.feedTabActive : ''}`}
                                onClick={() => handleFeedTabChange('trending-to-verify')}
                            >
                                Trending on Aluvo
                            </button>
                        </div>
                    )}

                    <div className={styles.searchBar}>
                        <span className={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder={activeTab === 'content' ? 'Search movies and shows…' : 'Search collectors…'}
                            className={styles.searchInput}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery('')} className={styles.clearSearch} aria-label="Clear search">
                                ✕
                            </button>
                        )}
                    </div>

                    {activeTab === 'content' && !searchQuery && (
                        <BrowseCategories
                            categories={categories}
                            activeCategoryId={categoryId}
                            activeCategory={activeCategory}
                            isCategoryView={isCategoryView}
                            isPickerOpen={categoryPickerOpen}
                            onTogglePicker={() => setCategoryPickerOpen(open => !open)}
                            onClear={() => {
                                setCategoryPickerOpen(false);
                                navigate('/discover');
                            }}
                            onSelect={(category) => {
                                setCategoryPickerOpen(false);
                                navigate(categoryId === category.id ? '/discover' : `/discover/category/${category.id}`);
                            }}
                        />
                    )}

                    {/* Type filter — only shown in Trending Now (not relevant for community rankings) */}
                    {activeTab === 'content' && (searchQuery || isCategoryView || feedTab === 'trending-now') && (
                        <div className={styles.filters}>
                            {[['all', 'All'], ['movie', '🎬 Movies'], ['tv', '📺 TV Shows']].map(([val, label]) => (
                                <button
                                    key={val}
                                    className={`${styles.filterBtn} ${activeFilter === val ? styles.active : ''}`}
                                    onClick={() => setActiveFilter(val)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Content */}
            <section className={styles.contentSection}>
                <div className={styles.container}>
                    <div className={styles.sectionTitleRow}>
                        <h2 className={styles.sectionTitle}>{sectionHeading}</h2>
                    </div>

                    {activeTab === 'content' && feedTab === 'aluvo-picks' && !searchQuery && !isCategoryView && (
                        <p className={styles.sectionHint}>Choose a list to start a guided watch path.</p>
                    )}

                    {activeTab === 'content' && !searchQuery && (feedTab === 'aluvo-picks' || isCategoryView) && (
                        <CuratedListRail
                            category={isCategoryView ? categoryId : undefined}
                            title={isCategoryView ? 'Featured Guide' : 'Aluvo Picks'}
                            showHeader={isCategoryView}
                            showTypeFilters={!isCategoryView}
                            emptyMessage={feedTab === 'aluvo-picks' ? 'No curated lists are published yet.' : null}
                        />
                    )}

                    {/* Top 5 promo: after Aluvo Picks, or between the heading and the
                        trending grid — never above the page's own discovery, and not
                        while searching, browsing a category or on the Users tab. */}
                    {activeTab === 'content' && !searchQuery && !isCategoryView && (
                        <TopMoviesDiscoverCard loggedIn={Boolean(user)} />
                    )}

                    {/* ══ TRENDING ON ALUVO TAB ══ */}
                    {activeTab === 'content' && feedTab === 'trending-to-verify' && !searchQuery && !isCategoryView && (
                        <>
                            {verifyLoading && (
                                <div className={styles.loadingState}>
                                    <div className={styles.spinner}>Loading…</div>
                                </div>
                            )}
                            {verifyError && (
                                <div className={styles.errorState}>
                                    <p className={styles.errorText}>⚠️ {verifyError}</p>
                                    <button className={styles.retryBtn} onClick={loadTrendingToVerify}>
                                        Try Again
                                    </button>
                                </div>
                            )}
                            {!verifyLoading && !verifyError && verifyFeed.length === 0 && (
                                <div className={styles.emptyState}>
                                    <span className={styles.emptyIcon}>🎬</span>
                                    <p className={styles.emptyText}>Coming soon</p>
                                    <p className={styles.emptyHint}>Verification activity is just getting started — check back soon!</p>
                                </div>
                            )}
                            {!verifyLoading && !verifyError && verifyFeed.length > 0 && filteredVerifyFeed.length === 0 && (
                                <div className={styles.emptyState}>
                                    <span className={styles.emptyIcon}>🎬</span>
                                    <p className={styles.emptyText}>No titles found</p>
                                    <p className={styles.emptyHint}>Try another media type filter</p>
                                </div>
                            )}
                            {!verifyLoading && !verifyError && filteredVerifyFeed.length > 0 && (
                                <div className={styles.verifyGrid}>
                                    {filteredVerifyFeed.map((item, index) => (
                                        <VerifyCard
                                            key={`${item.mediaId}-${index}`}
                                            item={item}
                                            onVerify={handleVerify}
                                            onOpenDetails={handleOpenDetails}
                                            isGuest={!user}
                                            sourceSection={sourceSection}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* ══ TRENDING NOW TAB + search results ══ */}
                    {activeTab === 'content' && (feedTab === 'trending-now' || searchQuery || isCategoryView) && (
                        <>
                            {loading && (
                                <div className={styles.loadingState}>
                                    <div className={styles.spinner}>Loading…</div>
                                </div>
                            )}
                            {error && (
                                <div className={styles.errorState}>
                                    <p className={styles.errorText}>⚠️ {error}</p>
                                    <button
                                        onClick={() => {
                                            setCurrentPage(1);
                                            setHasMore(true);
                                            isCategoryView ? loadCategory(categoryId, 1, true) : loadTrending(1, true);
                                        }}
                                        className={styles.retryBtn}
                                    >
                                        Try Again
                                    </button>
                                </div>
                            )}
                            {!loading && !error && filteredContent.length === 0 && (
                                <div className={styles.emptyState}>
                                    <span className={styles.emptyIcon}>🔍</span>
                                    <p className={styles.emptyText}>No results found</p>
                                    <p className={styles.emptyHint}>Try a different category, search, or filter</p>
                                </div>
                            )}
                            {!loading && !error && filteredContent.length > 0 && (
                                <>
                                    <div className={styles.grid}>
                                        {filteredContent.map((item, index) => (
                                            <ContentCard
                                                key={`${item.tmdbId}-${index}`}
                                                item={item}
                                                isInWatchlist={watchlistIds.has(item.tmdbId)}
                                                onWatchlist={handleToggleWatchlist}
                                                onAddToList={handleAddToList}
                                                onQuiz={handleQuizOrDetail}
                                                onOpenDetails={handleOpenDetails}
                                                isLastCard={index === filteredContent.length - 1}
                                                cardRef={lastCardRef}
                                                isGuest={!user}
                                                sourceSection={sourceSection}
                                            />
                                        ))}
                                    </div>
                                    {loadingMore && (
                                        <div className={styles.loadingMore}>
                                            <div className={styles.spinner}>Loading more…</div>
                                        </div>
                                    )}
                                    {!hasMore && filteredContent.length > 0 && (
                                        <div className={styles.endMessage}><p>🎉 You've seen everything!</p></div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* ══ USERS TAB ══ */}
                    {activeTab === 'users' && (
                        <>
                            {loading && (
                                <div className={styles.loadingState}>
                                    <div className={styles.spinner}>Loading…</div>
                                </div>
                            )}
                            {error && (
                                <div className={styles.errorState}>
                                    <p className={styles.errorText}>⚠️ {error}</p>
                                    <button onClick={loadUsers} className={styles.retryBtn}>Try Again</button>
                                </div>
                            )}
                            {!loading && !error && users.length === 0 && (
                                <div className={styles.emptyState}>
                                    <span className={styles.emptyIcon}>👥</span>
                                    <p className={styles.emptyText}>No collectors found</p>
                                    <p className={styles.emptyHint}>Try a different search</p>
                                </div>
                            )}
                            {!loading && !error && users.length > 0 && (
                                <div className={styles.usersGrid}>
                                    {users.map((userItem) => (
                                        <UserCard
                                            key={userItem.id}
                                            userItem={userItem}
                                            isFollowing={followingIds.has(userItem.id)}
                                            isCurrentUser={user?.id === userItem.id}
                                            onFollow={(e) => { e?.preventDefault?.(); handleToggleFollow(userItem.id); }}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                </div>
            </section>
        </main>
    );
}

export default Discover;
